import { useState, useEffect, useMemo } from "react"
import { authFetch } from "../../utils/api"
import BuscadorPaciente from "./BuscadorPaciente"
import BuscadorOC from "./BuscadorOC"

const API = process.env.REACT_APP_API_URL

const ESTADO_PACIENTE_LABEL = { ACTIVO: "activo", INACTIVO: "inactivo", BAJA: "baja" }
const ESTADO_ENTREGA_LABEL  = { PENDIENTE: "pendiente", DISTRIBUCION: "en distribución", ENTREGADO: "entregado", CANCELADO: "cancelado" }

const CAMPOS_OC = [
  { key: "obraSocial",           label: "Obra Social"          },
  { key: "apellido",             label: "Apellido"             },
  { key: "nombre",               label: "Nombre"               },
  { key: "dni",                  label: "DNI"                  },
  { key: "nroSocio",             label: "N° Socio"             },
  { key: "nroPedido",            label: "N° Pedido"            },
  { key: "nroOrdenCompra",       label: "N° Orden de Compra"   },
  { key: "fEmision",             label: "F. Emisión"           },
  { key: "fVencimiento",         label: "F. Vencimiento"       },
  { key: "tipoPlazo",            label: "Tipo Plazo"           },
  { key: "nroEntregas",          label: "Entregas pactadas"    },
  { key: "nroEntrega",           label: "N° Entrega"           },
  { key: "estadoEntrega",        label: "Estado Entrega"       },
  { key: "fEntrega",             label: "F. Entrega"           },
  { key: "observacionesEntrega", label: "Observaciones"        },
  { key: "nroFactura",           label: "N° Factura"           },
  { key: "nroNotaCredito",       label: "N° Nota de Crédito"   },
  { key: "nroRemito",            label: "N° Remito"            },
]

const CAMPOS_PACIENTE = [
  { key: "obraSocial",       label: "Obra Social"        },
  { key: "apellido",         label: "Apellido"           },
  { key: "nombre",           label: "Nombre"             },
  { key: "dni",              label: "DNI"                },
  { key: "nroSocio",         label: "N° Socio"           },
  { key: "nroCelular",       label: "N° Celular"         },
  { key: "estado",           label: "Estado"             },
  { key: "domicilio",        label: "Domicilio"          },
  { key: "localidad",        label: "Localidad"          },
  { key: "provincia",        label: "Provincia"          },
  { key: "medico",           label: "Médico"             },
  { key: "matricula",        label: "Matrícula"          },
  { key: "especialidad",     label: "Especialidad"       },
  { key: "enfAsignado",      label: "Enfermero"          },
  { key: "observaciones",    label: "Observaciones"      },
  { key: "productos",        label: "Productos"          },
  { key: "codigosProductos", label: "Código(s) Producto" },
]

function Reportes() {
  const [obrasSociales, setObrasSociales] = useState([])
  const [modo, setModo] = useState("general") // "general" | "paciente" | "orden"

  // ── Modo general ──────────────────────────────────────────────
  const [filtros, setFiltros] = useState({
    obraSocialId: "", fDesde: "", fHasta: "", estadoPaciente: "", estadoEntrega: "",
  })

  // ── Modo por paciente ─────────────────────────────────────────
  const [todosLosPacientes, setTodosLosPacientes] = useState(false)
  const [listaPacientes, setListaPacientes]       = useState([])
  const [cargandoPacientes, setCargandoPacientes] = useState(false)
  const [pacienteIds, setPacienteIds]             = useState([""])
  const [camposSeleccionados, setCamposSeleccionados] = useState(
    () => new Set(CAMPOS_PACIENTE.map(c => c.key))
  )

  // ── Modo por orden de compra ──────────────────────────────────
  const [todasLasOrdenes, setTodasLasOrdenes]     = useState(false)
  const [listaOrdenes, setListaOrdenes]           = useState([])
  const [cargandoOrdenes, setCargandoOrdenes]     = useState(false)
  const [ordenIds, setOrdenIds]                   = useState([""])
  const [camposOCSeleccionados, setCamposOCSeleccionados] = useState(
    () => new Set(CAMPOS_OC.map(c => c.key))
  )

  // ── Resultados ────────────────────────────────────────────────
  const [filas, setFilas]       = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError]       = useState(null)

  useEffect(() => {
    authFetch(`${API}/obras-sociales`).then(r => r.json()).then(setObrasSociales)
  }, [])

  useEffect(() => {
    if (modo !== "paciente") return
    setCargandoPacientes(true)
    authFetch(`${API}/pacientes`)
      .then(r => r.json())
      .then(setListaPacientes)
      .finally(() => setCargandoPacientes(false))
  }, [modo])

  useEffect(() => {
    if (modo !== "orden") return
    setCargandoOrdenes(true)
    authFetch(`${API}/reportes/lista-ordenes`)
      .then(r => r.json())
      .then(setListaOrdenes)
      .finally(() => setCargandoOrdenes(false))
  }, [modo])

  const handleModo = (m) => {
    setModo(m)
    setFilas(null)
    setError(null)
    setPacienteIds([""])
    setOrdenIds([""])
  }

  const handleFiltroChange = (e) => {
    setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setFilas(null)
    setError(null)
  }

  const toggleCampo = (key) => {
    setCamposSeleccionados(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleTodos = () => {
    setCamposSeleccionados(
      camposSeleccionados.size === CAMPOS_PACIENTE.length
        ? new Set()
        : new Set(CAMPOS_PACIENTE.map(c => c.key))
    )
  }

  const toggleCampoOC = (key) => {
    setCamposOCSeleccionados(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleTodosOC = () => {
    setCamposOCSeleccionados(
      camposOCSeleccionados.size === CAMPOS_OC.length
        ? new Set()
        : new Set(CAMPOS_OC.map(c => c.key))
    )
  }

  const handlePreview = async () => {
    setCargando(true)
    setError(null)
    setFilas(null)
    try {
      const params = new URLSearchParams()
      let url

      if (modo === "general") {
        if (filtros.obraSocialId)   params.set("obraSocialId",   filtros.obraSocialId)
        if (filtros.fDesde)         params.set("fDesde",         filtros.fDesde)
        if (filtros.fHasta)         params.set("fHasta",         filtros.fHasta)
        if (filtros.estadoPaciente) params.set("estadoPaciente", filtros.estadoPaciente)
        if (filtros.estadoEntrega)  params.set("estadoEntrega",  filtros.estadoEntrega)
        url = `${API}/reportes?${params}`
      } else if (modo === "paciente") {
        const idsValidos = pacienteIds.filter(Boolean)
        if (!todosLosPacientes && idsValidos.length > 0) params.set("pacienteIds", idsValidos.join(","))
        url = `${API}/reportes/pacientes?${params}`
      } else {
        const idsValidos = ordenIds.filter(Boolean)
        if (!todasLasOrdenes && idsValidos.length > 0) params.set("ordenIds", idsValidos.join(","))
        url = `${API}/reportes/ordenes?${params}`
      }

      const res = await authFetch(url)
      if (!res.ok) throw new Error()
      setFilas(await res.json())
    } catch {
      setError("No se pudo generar el reporte")
    } finally {
      setCargando(false)
    }
  }

  const resumenOrden = useMemo(() => {
    if (!filas || modo !== "orden") return null
    const ocsUnicas      = new Set(filas.map(f => f.nroOrdenCompra).filter(Boolean)).size
    const pacientesUnicos = new Set(filas.map(f => f.dni).filter(Boolean)).size
    const porEstado = { PENDIENTE: 0, DISTRIBUCION: 0, ENTREGADO: 0, CANCELADO: 0 }
    for (const f of filas) if (f.estadoEntrega) porEstado[f.estadoEntrega] = (porEstado[f.estadoEntrega] ?? 0) + 1
    return { ocsUnicas, pacientesUnicos, totalFilas: filas.length, porEstado }
  }, [filas, modo])

  const resumenGeneral = useMemo(() => {
    if (!filas || modo !== "general") return null
    const pacientesUnicos = new Set(filas.map(f => f.apellido + "|" + f.nombre + "|" + f.dni)).size
    const ocsUnicas       = new Set(filas.map(f => f.nroPedido).filter(Boolean)).size
    const porEstado = { PENDIENTE: 0, DISTRIBUCION: 0, ENTREGADO: 0, CANCELADO: 0 }
    for (const f of filas) porEstado[f.estadoEntrega] = (porEstado[f.estadoEntrega] ?? 0) + 1
    return { pacientesUnicos, ocsUnicas, totalEntregas: filas.length, porEstado }
  }, [filas, modo])

  const handleDescargar = () => {
    const SEP    = ";"
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`
    let lineas

    if (modo === "paciente") {
      const cols = CAMPOS_PACIENTE.filter(c => camposSeleccionados.has(c.key))
      lineas = [
        cols.map(c => escape(c.label)).join(SEP),
        ...filas.map(f => cols.map(c => escape(f[c.key])).join(SEP)),
      ]
    } else if (modo === "orden") {
      const cols = CAMPOS_OC.filter(c => camposOCSeleccionados.has(c.key))
      lineas = [
        cols.map(c => escape(c.label)).join(SEP),
        ...filas.map(f => cols.map(c => escape(f[c.key])).join(SEP)),
      ]
    } else {
      const headers = [
        "Obra Social", "Apellido", "Nombre", "DNI", "N° Socio", "Estado Paciente",
        "Domicilio", "Localidad", "Provincia", "Doctor", "Matrícula", "Especialidad",
        "N° Pedido", "N° OC", "F. Emisión", "F. Vencimiento", "Tipo Plazo",
        "N° Entrega", "Estado Entrega", "F. Entrega", "Observaciones",
        "N° Factura", "N° Nota de Crédito", "N° Remito",
      ]
      lineas = [
        headers.map(escape).join(SEP),
        ...filas.map(f =>
          [f.obraSocial, f.apellido, f.nombre, f.dni, f.nroSocio, f.estadoPaciente,
           f.domicilio, f.localidad, f.provincia, f.doctor, f.matricula, f.especialidad,
           f.nroPedido, f.nroOrdenCompra, f.fEmision, f.fVencimiento, f.tipoPlazo,
           f.nroEntrega, f.estadoEntrega, f.fEntrega, f.observacionesEntrega,
           f.nroFactura, f.nroNotaCredito, f.nroRemito].map(escape).join(SEP)
        ),
      ]
    }

    const blob = new Blob(["﻿" + lineas.join("\r\n")], { type: "text/csv;charset=utf-8" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href = url; a.download = `reporte-${new Date().toISOString().split("T")[0]}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  const camposFiltrados = CAMPOS_PACIENTE.filter(c => camposSeleccionados.has(c.key))

  return (
    <div className="adminPanel column">

      <div className="adminPanelTitle row">
        <i className="fa-solid fa-chart-bar"></i>
        REPORTES
      </div>

      <div className="reporteModoSelector row">
        <button
          className={`reporteModoBtn${modo === "general" ? " reporteModoBtn--active" : ""}`}
          onClick={() => handleModo("general")}
        >
          <i className="fa-solid fa-list"></i>
          reporte general
        </button>
        <button
          className={`reporteModoBtn${modo === "paciente" ? " reporteModoBtn--active" : ""}`}
          onClick={() => handleModo("paciente")}
        >
          <i className="fa-solid fa-user"></i>
          por paciente
        </button>
        <button
          className={`reporteModoBtn${modo === "orden" ? " reporteModoBtn--active" : ""}`}
          onClick={() => handleModo("orden")}
        >
          <i className="fa-solid fa-file-invoice"></i>
          por orden de compra
        </button>
      </div>

      {/* ── Modo general ─────────────────────────────────── */}
      {modo === "general" && (
        <div className="adminSection">
          <div className="adminSectionLabel">filtrar por</div>
          <div className="adminRow">
            <div className="adminField">
              <label>obra social</label>
              <select name="obraSocialId" value={filtros.obraSocialId} onChange={handleFiltroChange}>
                <option value="">todas</option>
                {obrasSociales.map(os => <option key={os.id} value={os.id}>{os.nombre}</option>)}
              </select>
            </div>
            <div className="adminField">
              <label>estado del paciente</label>
              <select name="estadoPaciente" value={filtros.estadoPaciente} onChange={handleFiltroChange}>
                <option value="">cualquier estado</option>
                {Object.entries(ESTADO_PACIENTE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="adminField">
              <label>estado de entrega</label>
              <select name="estadoEntrega" value={filtros.estadoEntrega} onChange={handleFiltroChange}>
                <option value="">cualquier estado</option>
                {Object.entries(ESTADO_ENTREGA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="adminRow">
            <div className="adminField">
              <label>OCs emitidas desde</label>
              <input type="date" name="fDesde" value={filtros.fDesde} onChange={handleFiltroChange} />
            </div>
            <div className="adminField">
              <label>OCs emitidas hasta</label>
              <input type="date" name="fHasta" value={filtros.fHasta} onChange={handleFiltroChange} />
            </div>
          </div>
        </div>
      )}

      {/* ── Modo por orden de compra ──────────────────────── */}
      {modo === "orden" && (
        <>
          <div className="adminSection">
            <div className="adminSectionLabel">orden de compra</div>

            <label className="reporteCampoCheck" style={{ marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={todasLasOrdenes}
                onChange={e => { setTodasLasOrdenes(e.target.checked); setOrdenIds([""]); setFilas(null) }}
              />
              exportar todas las órdenes de compra
            </label>

            {!todasLasOrdenes && (
              <div className="adminRow">
                <div className="adminField">
                  <label>buscar orden</label>
                  {cargandoOrdenes
                    ? <span className="adminHint">cargando...</span>
                    : (
                      <div className="column" style={{ gap: 6 }}>
                        {ordenIds.map((oid, idx) => {
                          const isLast   = idx === ordenIds.length - 1
                          const hasValue = !!oid
                          return (
                            <div key={idx} className="row" style={{ gap: 6, alignItems: "center" }}>
                              <BuscadorOC
                                ordenes={listaOrdenes}
                                value={oid}
                                onChange={id => {
                                  setOrdenIds(prev => {
                                    const next = [...prev]
                                    next[idx] = String(id)
                                    return next
                                  })
                                  setFilas(null)
                                }}
                              />
                              {ordenIds.length > 1 && (
                                <button
                                  className="usuarioEditBtn"
                                  title="quitar"
                                  onClick={() => setOrdenIds(prev => prev.filter((_, i) => i !== idx))}
                                >
                                  <i className="fa-solid fa-xmark" />
                                </button>
                              )}
                              {hasValue && isLast && (
                                <button
                                  className="adminBtn adminBtn--primary adminBtnAdd"
                                  title="agregar otra orden"
                                  onClick={() => setOrdenIds(prev => [...prev, ""])}
                                >
                                  +
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  }
                  <span className="adminHint">por n° OC, n° pedido o nombre del paciente</span>
                </div>
              </div>
            )}
          </div>

          <div className="adminSection">
            <div className="reporteCamposHeader row spacebetween">
              <span className="adminSectionLabel">campos a exportar</span>
              <button className="adminBtn adminBtnAdd" onClick={toggleTodosOC}>
                {camposOCSeleccionados.size === CAMPOS_OC.length ? "quitar todos" : "seleccionar todos"}
              </button>
            </div>
            <div className="reporteCamposGrid">
              {CAMPOS_OC.map(c => (
                <label key={c.key} className="reporteCampoCheck">
                  <input
                    type="checkbox"
                    checked={camposOCSeleccionados.has(c.key)}
                    onChange={() => toggleCampoOC(c.key)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Modo por paciente ─────────────────────────────── */}
      {modo === "paciente" && (
        <>
          <div className="adminSection">
            <div className="adminSectionLabel">paciente</div>

            <label className="reporteCampoCheck" style={{ marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={todosLosPacientes}
                onChange={e => { setTodosLosPacientes(e.target.checked); setPacienteIds([""]); setFilas(null) }}
              />
              exportar todos los pacientes
            </label>

            {!todosLosPacientes && (
              <div className="adminRow">
              <div className="adminField">
                <label>buscar paciente</label>
                {cargandoPacientes
                  ? <span className="adminHint">cargando...</span>
                  : (
                    <div className="column" style={{ gap: 6 }}>
                      {pacienteIds.map((pid, idx) => {
                        const isLast   = idx === pacienteIds.length - 1
                        const hasValue = !!pid
                        return (
                          <div key={idx} className="row" style={{ gap: 6, alignItems: "center" }}>
                            <BuscadorPaciente
                              pacientes={listaPacientes}
                              value={pid}
                              onChange={id => {
                                setPacienteIds(prev => {
                                  const next = [...prev]
                                  next[idx] = String(id)
                                  return next
                                })
                                setFilas(null)
                              }}
                            />
                            {pacienteIds.length > 1 && (
                              <button
                                className="usuarioEditBtn"
                                title="quitar"
                                onClick={() => setPacienteIds(prev => prev.filter((_, i) => i !== idx))}
                              >
                                <i className="fa-solid fa-xmark" />
                              </button>
                            )}
                            {hasValue && isLast && (
                              <button
                                className="adminBtn adminBtn--primary adminBtnAdd"
                                title="agregar otro paciente"
                                onClick={() => setPacienteIds(prev => [...prev, ""])}
                              >
                                +
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                }
                <span className="adminHint">por nombre, DNI o n° socio</span>
              </div>
              </div>
            )}
          </div>

          <div className="adminSection">
            <div className="reporteCamposHeader row spacebetween">
              <span className="adminSectionLabel">campos a exportar</span>
              <button className="adminBtn adminBtnAdd" onClick={toggleTodos}>
                {camposSeleccionados.size === CAMPOS_PACIENTE.length ? "quitar todos" : "seleccionar todos"}
              </button>
            </div>
            <div className="reporteCamposGrid">
              {CAMPOS_PACIENTE.map(c => (
                <label key={c.key} className="reporteCampoCheck">
                  <input
                    type="checkbox"
                    checked={camposSeleccionados.has(c.key)}
                    onChange={() => toggleCampo(c.key)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="adminBtnRow adminBtnRow--mb" style={{ justifyContent: "flex-start" }}>
        <button className="adminBtn adminBtn--primary" onClick={handlePreview} disabled={cargando}>
          {cargando ? "cargando..." : "vista previa"}
        </button>
        {filas && filas.length > 0 && (
          <button className="adminBtn" onClick={handleDescargar}>
            <i className="fa-solid fa-download" style={{ marginRight: 4 }}></i>
            descargar CSV
          </button>
        )}
      </div>

      {error && <div className="adminMensaje adminMensaje--error">{error}</div>}

      {filas && (
        <>
          {/* Resumen modo general */}
          {resumenGeneral && (
            <div className="reporteResumen row">
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.pacientesUnicos}</div>
                <div className="reporteResumenLabel">pacientes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.ocsUnicas}</div>
                <div className="reporteResumenLabel">órdenes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.totalEntregas}</div>
                <div className="reporteResumenLabel">entregas</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.porEstado.PENDIENTE}</div>
                <div className="reporteResumenLabel">pendientes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.porEstado.DISTRIBUCION}</div>
                <div className="reporteResumenLabel">distribución</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.porEstado.ENTREGADO}</div>
                <div className="reporteResumenLabel">entregados</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenGeneral.porEstado.CANCELADO}</div>
                <div className="reporteResumenLabel">cancelados</div>
              </div>
            </div>
          )}

          {/* Resumen modo por paciente */}
          {modo === "paciente" && (
            <div className="reporteResumen row">
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{filas.length}</div>
                <div className="reporteResumenLabel">pacientes</div>
              </div>
            </div>
          )}

          {/* Resumen modo por orden */}
          {resumenOrden && (
            <div className="reporteResumen row">
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.ocsUnicas}</div>
                <div className="reporteResumenLabel">órdenes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.pacientesUnicos}</div>
                <div className="reporteResumenLabel">pacientes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.porEstado.PENDIENTE}</div>
                <div className="reporteResumenLabel">pendientes</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.porEstado.DISTRIBUCION}</div>
                <div className="reporteResumenLabel">distribución</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.porEstado.ENTREGADO}</div>
                <div className="reporteResumenLabel">entregados</div>
              </div>
              <div className="reporteResumenCard">
                <div className="reporteResumenNumero">{resumenOrden.porEstado.CANCELADO}</div>
                <div className="reporteResumenLabel">cancelados</div>
              </div>
            </div>
          )}

          <div className="adminPreviewBox adminPreviewBox--scroll">
            {filas.length === 0 ? (
              <div className="adminEmptyState">no hay datos para los filtros seleccionados</div>
            ) : modo === "paciente" ? (
              <table className="adminPreviewTable">
                <thead>
                  <tr>{camposFiltrados.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i}>
                      {camposFiltrados.map(c => (
                        <td key={c.key}>
                          {c.key === "estado"
                            ? <span className={`usuarioRolBadge pacienteEstadoBadge--${f.estado?.toLowerCase()}`}>
                                {ESTADO_PACIENTE_LABEL[f.estado] ?? f.estado}
                              </span>
                            : f[c.key] || <span className="adminPlaceholder">—</span>
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : modo === "orden" ? (() => {
              const colsOC = CAMPOS_OC.filter(c => camposOCSeleccionados.has(c.key))
              return (
                <table className="adminPreviewTable">
                  <thead>
                    <tr>{colsOC.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filas.map((f, i) => (
                      <tr key={i}>
                        {colsOC.map(c => (
                          <td key={c.key}>
                            {c.key === "estadoEntrega" && f.estadoEntrega
                              ? <span className={`usuarioRolBadge entregaEstadoBadge--${f.estadoEntrega?.toLowerCase()}`}>
                                  {ESTADO_ENTREGA_LABEL[f.estadoEntrega] ?? f.estadoEntrega}
                                </span>
                              : f[c.key] || <span className="adminPlaceholder">—</span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            })() : (
              <table className="adminPreviewTable">
                <thead>
                  <tr>
                    <th>obra social</th><th>apellido</th><th>nombre</th><th>dni</th>
                    <th>n° socio</th><th>est. paciente</th>
                    <th>domicilio</th><th>localidad</th><th>provincia</th>
                    <th>doctor</th><th>matrícula</th><th>especialidad</th>
                    <th>n° pedido</th><th>n° oc</th><th>emisión</th><th>vencimiento</th><th>plazo</th>
                    <th>n° entrega</th><th>est. entrega</th><th>f. entrega</th><th>observaciones</th>
                    <th>n° factura</th><th>n° nc</th><th>n° remito</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i}>
                      <td>{f.obraSocial   || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.apellido     || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nombre       || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.dni          || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroSocio     || <span className="adminPlaceholder">—</span>}</td>
                      <td>
                        <span className={`usuarioRolBadge pacienteEstadoBadge--${f.estadoPaciente?.toLowerCase()}`}>
                          {ESTADO_PACIENTE_LABEL[f.estadoPaciente] ?? f.estadoPaciente}
                        </span>
                      </td>
                      <td>{f.domicilio    || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.localidad    || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.provincia    || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.doctor       || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.matricula    || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.especialidad || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroPedido        || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroOrdenCompra   || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.fEmision         || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.fVencimiento     || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.tipoPlazo?.toLowerCase() || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroEntrega       || <span className="adminPlaceholder">—</span>}</td>
                      <td>
                        {f.estadoEntrega
                          ? <span className={`usuarioRolBadge entregaEstadoBadge--${f.estadoEntrega?.toLowerCase()}`}>
                              {ESTADO_ENTREGA_LABEL[f.estadoEntrega] ?? f.estadoEntrega}
                            </span>
                          : <span className="adminPlaceholder">—</span>}
                      </td>
                      <td>{f.fEntrega             || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.observacionesEntrega  || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroFactura            || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroNotaCredito        || <span className="adminPlaceholder">—</span>}</td>
                      <td>{f.nroRemito             || <span className="adminPlaceholder">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

    </div>
  )
}

export default Reportes
